import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { AnalyticsService } from '../../consent/analytics.service';
import {
  Budget,
  ContactApiError,
  ContactRequest,
  ContactStatus,
  ProjectType,
} from '../../contact/contact.models';
import { ContactService } from '../../contact/contact.service';
import { Turnstile } from '../../contact/turnstile';

@Component({
  selector: 'app-contact',
  imports: [ReactiveFormsModule, RouterLink, Turnstile],
  templateUrl: './contact.html',
  styleUrl: './contact.scss',
})
export class Contact {
  protected readonly status = signal<ContactStatus>('idle');
  protected readonly submitError = signal('');
  protected readonly turnstileToken = signal('');
  protected readonly form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email, Validators.maxLength(254)],
    }),
    phone: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(30), Validators.pattern(/^[0-9+().\s-]*$/)],
    }),
    company: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(120)] }),
    projectType: new FormControl<ProjectType | ''>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    budget: new FormControl<Budget | ''>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    message: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(20), Validators.maxLength(3000)],
    }),
    privacyAccepted: new FormControl(false, {
      nonNullable: true,
      validators: [Validators.requiredTrue],
    }),
    website: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(0)] }),
  });

  private readonly contactService = inject(ContactService);
  private readonly analytics = inject(AnalyticsService);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly successMessage = viewChild<ElementRef<HTMLElement>>('successMessage');
  private readonly turnstile = viewChild(Turnstile);
  private startedAt = Date.now();

  protected updateTurnstileToken(token: string): void {
    this.turnstileToken.set(token);
  }

  protected submit(): void {
    this.submitError.set('');

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.submitError.set('Revisa los campos indicados antes de enviar el formulario.');
      queueMicrotask(() =>
        this.element.nativeElement.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
      );
      return;
    }

    if (!this.turnstileToken()) {
      this.submitError.set('Completa la verificación de seguridad antes de enviar.');
      return;
    }

    const value = this.form.getRawValue();
    const { projectType, budget } = value;
    if (!projectType || !budget) return;

    const request: ContactRequest = {
      ...value,
      projectType,
      budget,
      privacyAccepted: true,
      turnstileToken: this.turnstileToken(),
      startedAt: this.startedAt,
    };

    this.status.set('submitting');
    this.form.disable();
    this.contactService
      .submit(request)
      .pipe(
        finalize(() => {
          this.form.enable();
        }),
      )
      .subscribe({
        next: () => {
          this.analytics.trackLead();
          this.status.set('success');
          this.form.reset();
          this.startedAt = Date.now();
          this.turnstile()?.reset();
          queueMicrotask(() => this.successMessage()?.nativeElement.focus());
        },
        error: (error: unknown) => {
          this.status.set('error');
          this.submitError.set(contactErrorMessage(error));
          this.turnstile()?.reset();
        },
      });
  }
}

function contactErrorMessage(error: unknown): string {
  const kind = error instanceof ContactApiError ? error.kind : 'server';
  switch (kind) {
    case 'validation':
      return 'Los datos enviados no son válidos. Revisa el formulario e inténtalo de nuevo.';
    case 'verification':
      return 'No hemos podido completar la verificación de seguridad. Tus datos siguen en el formulario; completa de nuevo la verificación e inténtalo otra vez.';
    case 'rate-limit':
      return 'Has realizado varios intentos. Espera unos minutos antes de volver a enviar.';
    case 'network':
      return 'No hemos podido conectar con el servicio. Comprueba tu conexión e inténtalo de nuevo.';
    case 'server':
      return 'No hemos podido enviar el mensaje. Inténtalo de nuevo o utiliza el email, teléfono o WhatsApp.';
  }
}
